import { generatePath } from 'react-router-dom';
import type { SidebarNavItem, SidebarSection } from './types';
import type { PAYMENT_CONFIG } from 'state/paymentConfig/type';

export function isExternalUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export function generateNavPath(item: SidebarNavItem, orgId: string): string {
  if (item.external || isExternalUrl(item.path)) {
    if (!item.search) return item.path;

    // Handle query string concatenation properly for external URLs
    try {
      const url = new URL(item.path);
      const additionalParams = new URLSearchParams(
        item.search.replace(/^\?/, '')
      );
      additionalParams.forEach((value, key) => {
        url.searchParams.append(key, value);
      });
      return url.toString();
    } catch {
      // Fallback: use & if path already has ?, otherwise use ?
      const separator = item.path.includes('?') ? '&' : '?';
      return item.path + separator + item.search.replace(/^\?/, '');
    }
  }

  const basePath = `/:orgId/${item.layout}/${item.path}${item.search || ''}`;

  return generatePath(basePath, { orgId }).replace(/\/$/, '');
}

export function isNavItemActive(
  item: SidebarNavItem,
  currentPathname: string,
  orgId: string
): boolean {
  if (item.external || isExternalUrl(item.path)) {
    return false;
  }

  const itemPath = generateNavPath(item, orgId);
  const cleanItemPath = itemPath.split('?')[0];
  const cleanCurrentPath = currentPathname.split('?')[0];

  return cleanCurrentPath.startsWith(cleanItemPath);
}

export function hasActiveChild(
  item: SidebarNavItem,
  currentPathname: string,
  orgId: string
): boolean {
  if (!item.children?.length) {
    return false;
  }

  return item.children.some((child) =>
    isNavItemActive(child, currentPathname, orgId)
  );
}

export function checkNavItemAuthorization(
  item: SidebarNavItem,
  permissions?: PAYMENT_CONFIG.IEntityPermissionObject
): boolean {
  if (!item.requiredPermissions?.length) {
    return true;
  }

  return Boolean(
    permissions?.permissions?.length &&
      item.requiredPermissions.every((requiredPermission) =>
        permissions.permissions.includes(requiredPermission)
      )
  );
}

export function filterSectionItems(
  section: SidebarSection,
  permissions?: PAYMENT_CONFIG.IEntityPermissionObject
): SidebarSection {
  const filteredItems = section.items
    .map((item) => {
      if (item.children?.length) {
        const filteredChildren = item.children.filter((child) =>
          checkNavItemAuthorization(child, permissions)
        );

        if (filteredChildren.length === 0) {
          return null;
        }

        return {
          ...item,
          children: filteredChildren,
        };
      }

      if (!checkNavItemAuthorization(item, permissions)) {
        return null;
      }

      return item;
    })
    .filter((item): item is SidebarNavItem => item !== null);

  return {
    ...section,
    items: filteredItems,
  };
}

export function filterSectionsByOrgLevel(
  sections: SidebarSection[],
  isOrgLevel: boolean
): SidebarSection[] {
  if (!isOrgLevel) {
    return sections;
  }

  return sections.filter((section) => !section.hideAtOrgLevel);
}

export function getFilteredSections(
  sections: SidebarSection[],
  permissions?: PAYMENT_CONFIG.IEntityPermissionObject,
  isOrgLevel = false
): SidebarSection[] {
  const orgLevelFiltered = filterSectionsByOrgLevel(sections, isOrgLevel);

  const permissionFiltered = orgLevelFiltered
    .map((section) => filterSectionItems(section, permissions))
    .filter((section) => section.items.length > 0);

  return permissionFiltered;
}

export function getActiveClasses(isActive: boolean): string {
  return isActive ? 'bg-sidebar-active text-gray-blue-90 font-medium' : '';
}

export function getCollapsibleActiveClasses(isActive: boolean): string {
  return isActive ? 'bg-sidebar-active/40 text-gray-blue-90 font-medium' : '';
}

export function getNestedClasses(
  isNested: boolean,
  isCollapsed: boolean,
  isActive = false
): string {
  if (!isNested || isCollapsed) return '';

  const baseClasses = 'pl-8 ml-2 relative';
  const activeIndicator = isActive
    ? 'before:absolute before:left-0 before:top-1 before:bottom-1 before:w-0.5 before:bg-dark-green-link before:rounded-full bg-sidebar-active'
    : '';

  return `${baseClasses} ${activeIndicator}`.trim();
}

export function getCollapsedClasses(
  isCollapsed: boolean,
  isNested: boolean
): string {
  if (!isCollapsed) return '';
  return isNested ? 'justify-center px-0 py-1.5' : 'justify-center px-0';
}

export function getCollapsibleOpenClasses(
  isCollapsed: boolean,
  isOpen: boolean
): string {
  return isCollapsed && isOpen
    ? 'border-b border-gray-blue-20 rounded-b-none'
    : '';
}
